import React, { useRef, useEffect, useState } from 'react';
import './WoodBreaking.css';
import BeltPromotionModal from './BeltPromotionModal';
import PosePreloader from './PosePreloader';
import usePoseLoader from './hooks/usePoseLoader';
import { useRoturaImages } from './hooks/useRoturaImages';
import { drawOptimizedSkeleton, calculateGloveSize, calculateShoulderDistance } from './utils/skeletonUtils';
import useGameScreen from './hooks/useGameScreen';

const DIFFICULTY_PHASES = [
  { name: 'FÁCIL', color: '#00ff00', spawnRate: 1500, woodLife: 3000, moveChance: 0, goldChance: 0, bombChance: 0, reinforcedChance: 0 },
  { name: 'NORMAL', color: '#ffcc00', spawnRate: 1200, woodLife: 2500, moveChance: 0.2, goldChance: 0.05, bombChance: 0.08, reinforcedChance: 0.1 },
  { name: 'DIFÍCIL', color: '#ff6600', spawnRate: 900, woodLife: 2000, moveChance: 0.4, goldChance: 0.08, bombChance: 0.12, reinforcedChance: 0.15 },
  { name: 'EXTREMO', color: '#ff0000', spawnRate: 650, woodLife: 1600, moveChance: 0.6, goldChance: 0.12, bombChance: 0.15, reinforcedChance: 0.18 },
];

function WoodBreaking({ onClose, userData }) {
  useGameScreen();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);
  const [lives, setLives] = useState(3);
  const livesRef = useRef(3);
  const [round, setRound] = useState(1);
  const roundRef = useRef(1);
  const [roundWoods, setRoundWoods] = useState(0);
  const roundWoodsRef = useRef(0);
  const roundBrokenRef = useRef(0);
  const [showTransition, setShowTransition] = useState(false);
  const [transitionData, setTransitionData] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [isReady, setIsReady] = useState(false);
  const [combo, setCombo] = useState(0);
  const comboRef = useRef(0);
  const comboTimer = useRef(null);
  const woodsRef = useRef([]);
  const particlesRef = useRef([]);
  const feedbacksRef = useRef([]);
  const roturaImages = useRoturaImages();
  const prevHand = useRef({ r: [0, 0], l: [0, 0] });
  const prevFoot = useRef({ r: [0, 0], l: [0, 0] });
  const [showEnergyWarning, setShowEnergyWarning] = useState(false);
  const [showBeltModal, setShowBeltModal] = useState(false);
  const [beltPromotionData, setBeltPromotionData] = useState(null);
  const [diffPhase, setDiffPhase] = useState(0);
  const diffPhaseRef = useRef(0);
  const spawnIntervalRef = useRef(null);
  const roundTimerRef = useRef(null);
  const energyCost = 5;
  const WOODS_PER_ROUND = 10;

  const images = useRef({
    gloveHandDerecha: new Image(),
    gloveHandIzquierda: new Image(),
    gloveFoot: new Image(),
    gloveFootLeft: new Image(),
    background: new Image(),
    playerHead: new Image(),
    playerBody: new Image(),
    wood: new Image(),
    woodBomb: new Image(),
    armImage: new Image(),
    leftArmImage: new Image(),
    rightLegImage: new Image(),
    leftLegImage: new Image()
  });

  const audioRef = useRef(null);
  const onResultsRef = useRef(null);
  const shakeRef = useRef({ active: false, intensity: 0, time: 0 });
  const audioCtxRef = useRef(null);

  const getAudioCtx = () => {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return audioCtxRef.current;
  };

  const playBreakSound = (type) => {
    try {
      const ctx = getAudioCtx();
      if (ctx.state === 'suspended') ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      if (type === 'bomb') {
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(80, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(30, ctx.currentTime + 0.3);
        gain.gain.setValueAtTime(0.4, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.4);
      } else if (type === 'crack') {
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(400, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.08);
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.1);
      } else if (type === 'gold') {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(1760, ctx.currentTime + 0.08);
        gain.gain.setValueAtTime(0.25, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.3);
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2); gain2.connect(ctx.destination);
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(1320, ctx.currentTime + 0.05);
        gain2.gain.setValueAtTime(0.15, ctx.currentTime + 0.05);
        gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
        osc2.start(ctx.currentTime + 0.05);
        osc2.stop(ctx.currentTime + 0.35);
      } else if (type === 'hard' || type === 'reinforced') {
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(150, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 0.15);
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.2);
      } else {
        osc.type = 'square';
        osc.frequency.setValueAtTime(300, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.15);
      }
    } catch (e) { /* audio not supported */ }
  };

  const triggerShake = (intensity) => {
    shakeRef.current = { active: true, intensity, time: Date.now() };
  };

  useEffect(() => {
    if (!roturaImages.loading) {
      images.current.gloveHandDerecha.src = roturaImages.gloveHandDerecha;
      images.current.gloveHandIzquierda.src = roturaImages.gloveHandIzquierda;
      images.current.gloveFoot.src = roturaImages.gloveFoot;
      images.current.gloveFootLeft.src = roturaImages.gloveFootLeft;
      images.current.playerHead.src = roturaImages.playerHead;
      images.current.playerBody.src = roturaImages.playerBody;
    }
    images.current.background.src = '/img/backgroundmadera.png';
    images.current.wood.src = '/img/madera.png';
    images.current.woodBomb.src = '/img/maderaroja.png';
    images.current.armImage.src = '/img/rotura/brazoderecho.png';
    images.current.leftArmImage.src = '/img/rotura/brazoizquierdo.png';
    images.current.rightLegImage.src = '/img/rotura/piederecho.png';
    images.current.leftLegImage.src = '/img/rotura/pieizquierdo.png';
  }, [roturaImages]);

  const getPhase = () => DIFFICULTY_PHASES[diffPhaseRef.current] || DIFFICULTY_PHASES[0];

  const spawnWood = () => {
    const phase = getPhase();
    let cumulative = 0;
    let type = 'normal';
    cumulative += phase.goldChance;
    const rand = Math.random();
    if (rand < cumulative) { type = 'gold'; }
    else { cumulative += phase.bombChance; if (rand < cumulative) { type = 'bomb'; }
    else { cumulative += phase.reinforcedChance; if (rand < cumulative) { type = 'reinforced'; }
    else { cumulative += 0.2; if (rand < cumulative) { type = 'hard'; } } } }

    const moving = Math.random() < phase.moveChance;
    const newWood = {
      id: Date.now() + Math.random(),
      x: 0.15 + Math.random() * 0.7,
      y: 0.15 + Math.random() * 0.55,
      broken: false,
      type,
      hitsLeft: type === 'reinforced' ? 2 : 1,
      cracked: false,
      spawnTime: Date.now(),
      life: type === 'reinforced' ? phase.woodLife + 1000 : phase.woodLife,
      moving,
      vx: moving ? (Math.random() - 0.5) * 0.003 : 0,
      vy: moving ? (Math.random() - 0.5) * 0.002 : 0
    };
    woodsRef.current.push(newWood);
  };

  const getComboMultiplier = () => {
    const c = comboRef.current;
    if (c >= 12) return 5;
    if (c >= 7) return 3;
    if (c >= 3) return 2;
    return 1;
  };

  const createParticles = (x, y, colors, count = 20) => {
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count;
      const speed = 3 + Math.random() * 5;
      particlesRef.current.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2,
        life: 1,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 3 + Math.random() * 5
      });
    }
  };

  const addFeedback = (text, x, y, color) => {
    feedbacksRef.current.push({ text, x, y, color, time: Date.now() });
  };

  const updateParticles = () => {
    particlesRef.current = particlesRef.current.filter(p => {
      p.x += p.vx; p.y += p.vy; p.vx *= 0.98; p.vy += 0.4; p.life -= 0.015; p.size *= 0.97;
      return p.life > 0;
    });
  };

  const drawParticles = (ctx) => {
    particlesRef.current.forEach(p => {
      ctx.globalAlpha = p.life;
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    });
    ctx.globalAlpha = 1;
  };

  const drawFeedbacks = (ctx) => {
    const now = Date.now();
    feedbacksRef.current = feedbacksRef.current.filter(f => now - f.time < 900);
    feedbacksRef.current.forEach(f => {
      const elapsed = now - f.time;
      const progress = elapsed / 900;
      const yOff = progress * 70;
      const scale = 1 + progress * 0.2;
      const opacity = 1 - progress;
      ctx.save();
      ctx.scale(-1, 1);
      ctx.globalAlpha = opacity;
      ctx.shadowColor = f.color;
      ctx.shadowBlur = 20;
      ctx.fillStyle = f.color;
      ctx.font = `bold ${28 * scale}px Arial`;
      ctx.textAlign = 'center';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3;
      ctx.strokeText(f.text, -f.x, f.y - yOff);
      ctx.fillText(f.text, -f.x, f.y - yOff);
      ctx.restore();
    });
  };

  const drawWoods = (ctx, width, height) => {
    const now = Date.now();
    woodsRef.current = woodsRef.current.filter(wood => {
      if (wood.broken) return false;
      if (now - wood.spawnTime > wood.life) return false;

      // Movimiento
      if (wood.moving) {
        wood.x += wood.vx;
        wood.y += wood.vy;
        if (wood.x < 0.1 || wood.x > 0.9) wood.vx *= -1;
        if (wood.y < 0.1 || wood.y > 0.75) wood.vy *= -1;
      }

      const age = (now - wood.spawnTime) / wood.life;
      const pulse = Math.sin(age * 10) * 0.1 + 0.9;
      const x = width * wood.x;
      const y = height * wood.y;
      const sizeMap = { hard: 100, gold: 90, bomb: 85, reinforced: 95, normal: 80 };
      const size = sizeMap[wood.type] || 80;

      const remaining = 1 - age;
      const blink = remaining < 0.3 ? (Math.sin(now / 80) > 0 ? 1 : 0.3) : 1;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(wood.type === 'bomb' ? 0 : age * 2);
      ctx.globalAlpha = pulse * blink;

      if (wood.type === 'bomb') {
        const bombPulse = Math.sin(now / 120) * 0.08 + 1;
        const bSize = size * bombPulse;
        if (images.current.woodBomb.complete && images.current.woodBomb.naturalWidth > 0) {
          ctx.shadowBlur = 15;
          ctx.shadowColor = '#FF0000';
          ctx.drawImage(images.current.woodBomb, -bSize / 2, -bSize / 2, bSize, bSize);
          ctx.shadowBlur = 0;
        } else {
          ctx.shadowBlur = 15;
          ctx.shadowColor = '#FF0000';
          ctx.fillStyle = '#8B0000';
          ctx.fillRect(-bSize / 2, -bSize / 2, bSize, bSize);
          ctx.shadowBlur = 0;
        }
        ctx.strokeStyle = '#FF0000';
        ctx.lineWidth = 3;
        ctx.strokeRect(-bSize / 2, -bSize / 2, bSize, bSize);
      } else if (images.current.wood.complete && images.current.wood.naturalWidth > 0) {
        ctx.drawImage(images.current.wood, -size / 2, -size / 2, size, size);
        if (wood.type === 'hard') {
          ctx.fillStyle = 'rgba(100,50,0,0.3)';
          ctx.fillRect(-size / 2, -size / 2, size, size);
        } else if (wood.type === 'gold') {
          ctx.fillStyle = 'rgba(255,215,0,0.35)';
          ctx.fillRect(-size / 2, -size / 2, size, size);
          ctx.shadowBlur = 20;
          ctx.shadowColor = '#FFD700';
          ctx.strokeStyle = '#FFD700';
          ctx.lineWidth = 3;
          ctx.strokeRect(-size / 2, -size / 2, size, size);
          ctx.shadowBlur = 0;
        } else if (wood.type === 'reinforced') {
          // Refuerzo metálico
          ctx.fillStyle = wood.cracked ? 'rgba(255,80,0,0.25)' : 'rgba(120,120,120,0.35)';
          ctx.fillRect(-size / 2, -size / 2, size, size);
          ctx.strokeStyle = wood.cracked ? '#FF4400' : '#888';
          ctx.lineWidth = 3;
          ctx.strokeRect(-size / 2, -size / 2, size, size);
          // Grieta si ya recibió un golpe
          if (wood.cracked) {
            ctx.strokeStyle = '#FF2200';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(-size / 4, -size / 4);
            ctx.lineTo(3, 0);
            ctx.lineTo(size / 4, size / 4);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(size / 5, -size / 3);
            ctx.lineTo(-2, 5);
            ctx.lineTo(-size / 5, size / 3);
            ctx.stroke();
          }
        }
      } else {
        const colorMap = { normal: '#8B4513', hard: '#654321', gold: '#DAA520', reinforced: '#666', bomb: '#1a1a1a' };
        ctx.fillStyle = colorMap[wood.type] || '#8B4513';
        ctx.fillRect(-size / 2, -size / 2, size, size);
      }

      // Labels
      ctx.rotate(wood.type === 'bomb' ? 0 : -age * 2);
      ctx.scale(-1, 1);
      ctx.textAlign = 'center';
      if (wood.type === 'gold') {
        ctx.fillStyle = '#FFD700';
        ctx.font = 'bold 14px Arial';
        ctx.fillText('⭐ ORO', 0, -size / 2 - 8);
      } else if (wood.type === 'hard') {
        ctx.fillStyle = '#FF6600';
        ctx.font = 'bold 12px Arial';
        ctx.fillText('DURA', 0, -size / 2 - 8);
      } else if (wood.type === 'bomb') {
        ctx.fillStyle = '#FF0000';
        ctx.font = 'bold 13px Arial';
        ctx.fillText('💣 NO TOCAR', 0, -size / 2 - 12);
      } else if (wood.type === 'reinforced') {
        ctx.fillStyle = wood.cracked ? '#FF4400' : '#AAAAAA';
        ctx.font = 'bold 12px Arial';
        ctx.fillText(wood.cracked ? '¡1 MÁS!' : '🔩 x2', 0, -size / 2 - 8);
      }

      ctx.restore();
      return true;
    });
  };

  const drawDifficultyBar = (ctx, width) => {
    const phase = getPhase();
    ctx.save();
    ctx.scale(-1, 1);
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(-width / 2 - 60, 55, 120, 24);
    ctx.fillStyle = phase.color;
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(phase.name, -width / 2, 72);
    ctx.restore();
  };

  const drawComboIndicator = (ctx, width) => {
    if (comboRef.current < 3) return;
    const mult = getComboMultiplier();
    ctx.save();
    ctx.scale(-1, 1);
    const pulse = Math.sin(Date.now() / 150) * 0.15 + 1;
    const colors = { 2: '#FFAA00', 3: '#FF6600', 5: '#FF0000' };
    const color = colors[mult] || '#FFAA00';
    ctx.fillStyle = color;
    ctx.shadowColor = color;
    ctx.shadowBlur = 15;
    ctx.font = `bold ${22 * pulse}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(`🔥 x${mult} COMBO (${comboRef.current})`, -width / 2, 105);
    ctx.shadowBlur = 0;
    ctx.restore();
  };

  const drawSkeleton = (ctx, landmarks, width, height) => {
    return drawOptimizedSkeleton(ctx, landmarks, width, height, images.current);
  };

  const drawGloves = (ctx, landmarks, width, height, shoulderDist) => {
    if (!landmarks) return;
    const gloveSize = calculateGloveSize(shoulderDist);
    const gloves = [
      { img: images.current.gloveHandDerecha, landmark: landmarks[20] },
      { img: images.current.gloveHandIzquierda, landmark: landmarks[19] },
      { img: images.current.gloveFootLeft, landmark: landmarks[32] },
      { img: images.current.gloveFoot, landmark: landmarks[31] }
    ];
    gloves.forEach(({ img, landmark }) => {
      if (landmark?.visibility > 0.5 && img?.complete && img.naturalWidth > 0) {
        const x = width * landmark.x;
        const y = height * landmark.y;
        ctx.globalAlpha = 0.9;
        ctx.drawImage(img, x - gloveSize / 2, y - gloveSize / 2, gloveSize, gloveSize);
        ctx.globalAlpha = 1.0;
      }
    });
  };

  const detectHit = (landmarks, width, height) => {
    if (!landmarks) return;
    // index 0-1 = hands, 2-3 = feet
    const limbs = [
      { x: landmarks[20].x, y: landmarks[20].y, prevX: prevHand.current.r[0], prevY: prevHand.current.r[1], isKick: false },
      { x: landmarks[19].x, y: landmarks[19].y, prevX: prevHand.current.l[0], prevY: prevHand.current.l[1], isKick: false },
      { x: landmarks[32].x, y: landmarks[32].y, prevX: prevFoot.current.r[0], prevY: prevFoot.current.r[1], isKick: true },
      { x: landmarks[31].x, y: landmarks[31].y, prevX: prevFoot.current.l[0], prevY: prevFoot.current.l[1], isKick: true }
    ];
    prevHand.current = { r: [landmarks[20].x, landmarks[20].y], l: [landmarks[19].x, landmarks[19].y] };
    prevFoot.current = { r: [landmarks[32].x, landmarks[32].y], l: [landmarks[31].x, landmarks[31].y] };

    limbs.forEach(limb => {
      const speed = Math.sqrt((limb.x - limb.prevX) ** 2 + (limb.y - limb.prevY) ** 2);
      if (speed < 0.02) return;

      woodsRef.current.forEach(wood => {
        if (wood.broken) return;
        const dist = Math.sqrt((limb.x - wood.x) ** 2 + (limb.y - wood.y) ** 2);
        if (dist < 0.1) {

          // BOMBA: pierde vida
          if (wood.type === 'bomb') {
            wood.broken = true;
            livesRef.current = Math.max(livesRef.current - 1, 0);
            setLives(livesRef.current);
            comboRef.current = 0;
            setCombo(0);
            clearTimeout(comboTimer.current);
            createParticles(width * wood.x, height * wood.y, ['#FF0000', '#FF3300', '#000000', '#FF6600'], 35);
            playBreakSound('bomb');
            triggerShake(12);
            addFeedback('💣 BOMBA! -1❤️', width * wood.x, height * wood.y, '#FF0000');
            if (livesRef.current <= 0) {
              setGameOver(true);
              clearInterval(spawnIntervalRef.current);
              clearTimeout(roundTimerRef.current);
              handleGameEnd();
            }
            return;
          }

          // REFORZADA: necesita 2 golpes
          if (wood.type === 'reinforced' && wood.hitsLeft > 1) {
            wood.hitsLeft -= 1;
            wood.cracked = true;
            createParticles(width * wood.x, height * wood.y, ['#999', '#AAA', '#777'], 10);
            playBreakSound('crack');
            triggerShake(3);
            addFeedback('💥 AGRIETADA', width * wood.x, height * wood.y, '#AAAAAA');
            return;
          }

          wood.broken = true;

          // Puntos base según tipo
          const pointsMap = { hard: 15, gold: 30, reinforced: 25, normal: 10 };
          let basePoints = pointsMap[wood.type] || 10;
          if (limb.isKick) basePoints = Math.round(basePoints * 1.5);
          const mult = getComboMultiplier();
          const points = basePoints * mult;

          scoreRef.current += points;
          setScore(scoreRef.current);

          roundBrokenRef.current += 1;

          const particleColorMap = {
            gold: ['#FFD700', '#FFA500', '#FFFF00', '#FFE066'],
            reinforced: ['#888', '#AAA', '#666', '#8B4513'],
            default: ['#8B4513', '#A0522D', '#D2691E', '#CD853F']
          };
          const particleColors = particleColorMap[wood.type] || particleColorMap.default;
          createParticles(width * wood.x, height * wood.y, particleColors, wood.type === 'gold' ? 40 : 25);

          playBreakSound(wood.type);
          const shakeIntensity = wood.type === 'gold' ? 8 : (wood.type === 'reinforced' ? 6 : (comboRef.current >= 7 ? 6 : (comboRef.current >= 3 ? 4 : 2)));
          triggerShake(shakeIntensity);

          const hitLabel = limb.isKick ? '🦶 PATADA' : '👊 PUÑO';
          const feedColor = wood.type === 'gold' ? '#FFD700' : (wood.type === 'reinforced' ? '#AAAAAA' : (limb.isKick ? '#00BFFF' : '#00FF00'));
          addFeedback(`${hitLabel} +${points}`, width * wood.x, height * wood.y, feedColor);
          if (wood.type === 'gold') addFeedback('⭐ ORO!', width * wood.x, height * wood.y - 30, '#FFD700');
          if (wood.type === 'reinforced') addFeedback('🔩 DESTRUIDA!', width * wood.x, height * wood.y - 30, '#FF6600');

          comboRef.current += 1;
          setCombo(comboRef.current);
          clearTimeout(comboTimer.current);
          comboTimer.current = setTimeout(() => {
            comboRef.current = 0;
            setCombo(0);
          }, 2000);
        }
      });
    });
  };

  const onResults = (results) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video || video.videoWidth === 0) return;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Screen shake
    if (shakeRef.current.active) {
      const elapsed = Date.now() - shakeRef.current.time;
      if (elapsed < 200) {
        const decay = 1 - elapsed / 200;
        const i = shakeRef.current.intensity * decay;
        ctx.translate((Math.random() - 0.5) * i * 2, (Math.random() - 0.5) * i * 2);
      } else {
        shakeRef.current.active = false;
      }
    }

    if (images.current.background.complete) {
      ctx.save();
      ctx.scale(-1, 1);
      ctx.drawImage(images.current.background, -canvas.width, 0, canvas.width, canvas.height);
      ctx.restore();
    }
    drawWoods(ctx, canvas.width, canvas.height);
    if (results.poseLandmarks) {
      const shoulderDist = drawSkeleton(ctx, results.poseLandmarks, canvas.width, canvas.height);
      drawGloves(ctx, results.poseLandmarks, canvas.width, canvas.height, shoulderDist);
      detectHit(results.poseLandmarks, canvas.width, canvas.height);
    }
    updateParticles();
    drawParticles(ctx);
    drawFeedbacks(ctx);
    drawDifficultyBar(ctx, canvas.width);
    drawComboIndicator(ctx, canvas.width);
    ctx.restore();
  };

  onResultsRef.current = onResults;
  const poseLoader = usePoseLoader((...args) => onResultsRef.current?.(...args));

  useEffect(() => {
    const checkEnergy = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/training/start`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ energyCost })
        });
        if (!response.ok) {
          setShowEnergyWarning(true);
          setTimeout(onClose, 3000);
          return;
        }
      } catch (error) {
        console.error('Error al verificar energía:', error);
      }
    };
    checkEnergy();
    if (videoRef.current) poseLoader.start(videoRef.current);
    return () => {
      poseLoader.stop();
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
      if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
    };
  }, []);

  const startRound = () => {
    const r = roundRef.current;
    const phaseIdx = Math.min(Math.floor((r - 1) / 2), DIFFICULTY_PHASES.length - 1);
    diffPhaseRef.current = phaseIdx;
    setDiffPhase(phaseIdx);
    roundWoodsRef.current = 0;
    roundBrokenRef.current = 0;
    setRoundWoods(0);
    woodsRef.current = [];
    setShowTransition(false);
    const phase = DIFFICULTY_PHASES[phaseIdx];
    let spawned = 0;
    const totalWoods = WOODS_PER_ROUND + Math.floor((r - 1) * 2);
    clearInterval(spawnIntervalRef.current);
    spawnIntervalRef.current = setInterval(() => {
      if (spawned >= totalWoods || livesRef.current <= 0) {
        clearInterval(spawnIntervalRef.current);
        return;
      }
      spawnWood();
      spawned++;
      roundWoodsRef.current = spawned;
      setRoundWoods(spawned);
    }, phase.spawnRate);
    // Chequear fin de ronda periódicamente
    clearTimeout(roundTimerRef.current);
    const checkEnd = () => {
      if (livesRef.current <= 0) return;
      const allSpawned = spawned >= totalWoods;
      const allGone = woodsRef.current.length === 0;
      if (allSpawned && allGone) {
        endRound(totalWoods);
      } else {
        roundTimerRef.current = setTimeout(checkEnd, 500);
      }
    };
    roundTimerRef.current = setTimeout(checkEnd, 2000);
  };

  const endRound = (totalWoods) => {
    clearInterval(spawnIntervalRef.current);
    const broken = roundBrokenRef.current;
    setTransitionData({ round: roundRef.current, broken, total: totalWoods, score: scoreRef.current, lives: livesRef.current });
    setShowTransition(true);
    roundTimerRef.current = setTimeout(() => {
      if (livesRef.current <= 0) return;
      roundRef.current += 1;
      setRound(roundRef.current);
      startRound();
    }, 3000);
  };

  // Countdown e inicio
  useEffect(() => {
    if (!poseLoader.loaded) return;
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().catch(() => {});
    }
    const countdownTimer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownTimer);
          setIsReady(true);
          startRound();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      clearInterval(spawnIntervalRef.current);
      clearTimeout(roundTimerRef.current);
      clearInterval(countdownTimer);
    };
  }, [poseLoader.loaded]);

  const handleGameEnd = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/training/finish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          score: scoreRef.current,
          tiempo: 60,
          precision: 0,
          modo: 'rotura'
        })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.subiDeNivel && data.beltData) {
          setBeltPromotionData({ cinturon: data.cinturon, beltData: data.beltData });
          setTimeout(() => setShowBeltModal(true), 2500);
        }
      }
    } catch (error) {
      console.error('Error al guardar rotura:', error);
    }
  };

  const handleCloseBeltModal = () => {
    setShowBeltModal(false);
    setBeltPromotionData(null);
    if (onClose) onClose();
  };

  const comboMult = getComboMultiplier();

  return (
    <div className="wood-breaking-overlay">
      <div className="wood-breaking-container">
        <audio ref={audioRef} src="/audio/maderamusica.mp3" loop />
        <video ref={videoRef} style={{ display: 'none' }} />
        <canvas ref={canvasRef} className="game-canvas" />
        {!poseLoader.loaded && (
          <PosePreloader progress={poseLoader.progress} statusText={poseLoader.statusText} title="ROTURA" hint="Dejá el celular en un lugar fijo sin que se caiga. Alejate hasta que te tome el cuerpo completo. Rompé las maderas antes de que desaparezcan. Las doradas dan más puntos, las duras necesitan 2 golpes. ¡No toques las rojas!" />
        )}
        <button className="close-btn" onClick={onClose}>✕</button>
        <div className="game-header">
          <div className="stat">{'❤️'.repeat(lives)}{'🖤'.repeat(3 - lives)}</div>
          <div className="stat">🥋 Ronda {round}</div>
          <div className="stat">🎯 {score}</div>
          {combo >= 3 && <div className="stat combo">🔥 x{comboMult}</div>}
        </div>
        {!isReady && countdown > 0 && (
          <div className="countdown-overlay">
            <div className="countdown-number">{countdown}</div>
          </div>
        )}
        {showEnergyWarning && (
          <div className="game-over">
            <h3>⚠️ ENERGÍA INSUFICIENTE</h3>
            <p>Necesitas {energyCost} de energía para jugar</p>
          </div>
        )}
        {showTransition && transitionData && !gameOver && (
          <div className="round-transition">
            <h3>RONDA {transitionData.round} COMPLETADA</h3>
            <p>Maderas rotas: {transitionData.broken} / {transitionData.total}</p>
            <p className="transition-score">Score: {transitionData.score}</p>
            <p>Vidas: {'❤️'.repeat(transitionData.lives)}</p>
            <p className="next-round">Siguiente ronda...</p>
          </div>
        )}
        {gameOver && !showBeltModal && !beltPromotionData && (
          <div className="game-over">
            <h3>💀 GAME OVER</h3>
            <p>Llegaste a la ronda {round}</p>
            <h1>{score}</h1>
            <button className="menu-btn" onClick={onClose}>VOLVER</button>
          </div>
        )}
        {showBeltModal && beltPromotionData && (
          <BeltPromotionModal
            cinturon={beltPromotionData.cinturon}
            beltData={beltPromotionData.beltData}
            onClose={handleCloseBeltModal}
          />
        )}
      </div>
    </div>
  );
}

export default WoodBreaking;
